import { isSharedPlayback } from '../../api/libraryPlayback';
import { getKnownCacheEpoch } from '../../api/cacheEpochApi';
import { isOfflineCacheCurrent } from '../../api/offlineCache';
import { BedtimePair } from '../../context/bedtime/bedtimeContextModel';

export const STORY_MODE_SESSION_PREFIX = 'luma:story-mode-session:v1:';
export const STORY_MODE_PRESTART_TTL_MS = 30 * 60 * 1000;
export const STORY_MODE_ACTIVE_TTL_MS = 4 * 60 * 60 * 1000;

export type StoryModeValidationMode = 'online' | 'offline_cache';

export interface StoryModeSessionV1 {
  version: 1;
  userId: string;
  householdId: string;
  listenerId: string;
  voiceId: string;
  initialRenderId: string;
  allowedRenderIds: string[];
  queueRenderIds: string[]; // zero for a single story, otherwise 2–6
  offlineReadyRenderIds: string[];
  validationMode: StoryModeValidationMode;
  createdAt: string;
  nonce: string;
  startedAt?: string;
}

function key(userId: string): string { return `${STORY_MODE_SESSION_PREFIX}${userId}`; }

export function buildStoryModeSession(
  pair: BedtimePair,
  initialRenderId: string,
  allowedRenderIds: string[],
  options: {
    userId: string;
    queueRenderIds?: string[];
    offlineReadyRenderIds?: string[];
    validationMode?: StoryModeValidationMode;
    now?: Date;
  }
): StoryModeSessionV1 {
  const allowed = Array.from(new Set(allowedRenderIds));
  const queue = Array.from(new Set(options.queueRenderIds || [])).filter((id) => allowed.includes(id));
  const offlineReady = Array.from(new Set(options.offlineReadyRenderIds || [])).filter((id) => allowed.includes(id));
  const now = options.now || new Date();
  if (!options.userId || !allowed.includes(initialRenderId)) throw new Error('The initial story is not in the approved handoff.');
  if (queue.length === 1 || queue.length > 6) throw new Error('A Story Mode queue must contain two to six stories.');
  if (queue.length > 0 && !queue.includes(initialRenderId)) throw new Error('A Story Mode queue must contain the initial story.');
  const nonce = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : String(now.getTime().toString(36)) + '-' + Math.random().toString(36).slice(2, 10);
  return {
    version: 1,
    userId: options.userId,
    householdId: pair.householdId,
    listenerId: pair.listenerId,
    voiceId: pair.voiceId,
    initialRenderId,
    allowedRenderIds: allowed,
    queueRenderIds: queue,
    offlineReadyRenderIds: offlineReady,
    validationMode: options.validationMode || 'online',
    createdAt: now.toISOString(),
    nonce,
  };
}

export function writeStoryModeSession(userId: string, session: StoryModeSessionV1): void {
  try { sessionStorage.setItem(key(userId), JSON.stringify(session)); } catch { /* no-op */ }
}

export function clearStoryModeSession(userId: string): void {
  try { sessionStorage.removeItem(key(userId)); } catch { /* no-op */ }
}

export function readStoryModeSession(
  userId: string,
  phase: 'prestart' | 'active' | 'auto' = 'prestart',
  now = new Date()
): StoryModeSessionV1 | null {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(key(userId)) || 'null') as StoryModeSessionV1 | null;
    const stringArray = (value: unknown): value is string[] => Array.isArray(value) && value.every((item) => typeof item === 'string' && item.length > 0);
    if (!parsed || parsed.version !== 1 || parsed.userId !== userId || !parsed.householdId || !parsed.listenerId || !parsed.voiceId || !parsed.initialRenderId || !parsed.nonce || !stringArray(parsed.allowedRenderIds) || !stringArray(parsed.queueRenderIds) || !stringArray(parsed.offlineReadyRenderIds) || !['online', 'offline_cache'].includes(parsed.validationMode)) {
      clearStoryModeSession(userId);
      return null;
    }
    const created = Date.parse(parsed.createdAt);
    const started = parsed.startedAt ? Date.parse(parsed.startedAt) : null;
    if (!Number.isFinite(created)
      || (parsed.startedAt && (!Number.isFinite(started)
        || (started as number) < created - 60_000
        || (started as number) - created > STORY_MODE_PRESTART_TTL_MS))) {
      clearStoryModeSession(userId);
      return null;
    }
    const active = phase === 'active' || (phase === 'auto' && Boolean(parsed.startedAt));
    const reference = active && started != null ? started : created;
    const ttl = active ? STORY_MODE_ACTIVE_TTL_MS : STORY_MODE_PRESTART_TTL_MS;
    if (now.getTime() - reference > ttl || now.getTime() < reference - 60_000) {
      clearStoryModeSession(userId);
      return null;
    }
    if (!parsed.allowedRenderIds.includes(parsed.initialRenderId)
      || parsed.queueRenderIds.length === 1
      || parsed.queueRenderIds.length > 6
      || (parsed.queueRenderIds.length > 0 && !parsed.queueRenderIds.includes(parsed.initialRenderId))
      || new Set(parsed.allowedRenderIds).size !== parsed.allowedRenderIds.length
      || new Set(parsed.queueRenderIds).size !== parsed.queueRenderIds.length
      || new Set(parsed.offlineReadyRenderIds).size !== parsed.offlineReadyRenderIds.length
      || parsed.queueRenderIds.some((id) => !parsed.allowedRenderIds.includes(id))
      || parsed.offlineReadyRenderIds.some((id) => !parsed.allowedRenderIds.includes(id))) {
      clearStoryModeSession(userId);
      return null;
    }
    return parsed;
  } catch {
    clearStoryModeSession(userId);
    return null;
  }
}

export function markStoryModeStarted(userId: string, now = new Date()): StoryModeSessionV1 | null {
  const session = readStoryModeSession(userId, 'auto', now);
  if (!session) return null;
  if (session.startedAt) return session;
  const started = { ...session, startedAt: now.toISOString() };
  writeStoryModeSession(userId, started);
  return started;
}

export function sessionAllowsRender(session: StoryModeSessionV1, renderId: string, offline = false): boolean {
  if (!session.allowedRenderIds.includes(renderId)) return false;
  // A network transition can happen after an online handoff. Offline playback
  // is therefore always limited to the parent-confirmed current-epoch cache
  // allow-list, regardless of how the session was initially validated.
  return !offline || (!isSharedPlayback(renderId) && session.offlineReadyRenderIds.includes(renderId)
    && isOfflineCacheCurrent(session.userId, renderId, session.householdId,
      session.listenerId, session.voiceId, getKnownCacheEpoch(session.householdId)));
}
